/**
 * Alert definition as a list-row bell shows it (PDW-240 / BE PDW-241): no episode data, just which alert
 * matched. Mirrors `AlertRowFlagDefinitionDto` of the BE until the generated schema carries it. A shared
 * alert is a filter owned by the technical user GLOBAL, hence `isGlobal` rather than a scope enum.
 */
export type AlertRowFlagDefinitionDto = {
  userFilterId: number;
  name: string;
  isGlobal: boolean;
  /** 0 to 100 */
  weight: number;
};

/** A list row enriched with its alert bell: `alerts` is ordered by weight descending by the BE and is empty
 * (never null) when the row matches nothing, the user lacks the Use alerts right or alerts are switched off. */
export type AlertFlaggedRow = {
  alerts?: AlertRowFlagDefinitionDto[];
};

/* ---------------------------------------------------------------------------------------------------------
 * Episodes (PDW-245 Alerts panel, BE PDW-179 dispositions + ticket 5 panel read). The enums are core's
 * `AlertEpisodeDisplayedState`, `AlertEpisodeAction` and `AlertDisposition`; the panel DTO follows
 * `ALERT_EPISODE_PANEL.md` §4.1 with the delivered-model corrections (`isGlobal` instead of a scope enum,
 * the anchor type as a string) and the panel-level `snoozeOptions` of TASK §7.1.
 * ------------------------------------------------------------------------------------------------------- */

/** Displayed state = the disposition if one is set, otherwise the match state; derived by the BE at read time. */
export type AlertEpisodeDisplayedState = 'ACTIVE' | 'SNOOZED' | 'SOLVED' | 'INACTIVE' | 'DELETED';
/** The non-active displayed states; the panel keeps them collapsed and hides the weight badge on them. */
export const ALERT_EPISODE_NOT_ACTIVE_STATES: AlertEpisodeDisplayedState[] = ['SOLVED', 'INACTIVE', 'DELETED'];

/** What the user may do with an episode; computed by the BE from the displayed state alone. */
export type AlertEpisodeAction = 'SOLVE' | 'SNOOZE' | 'DELETE' | 'RESTORE' | 'UNSNOOZE';

/** The disposition a user posts. Restore and un-snooze both send NONE. */
export type AlertDisposition = 'NONE' | 'SNOOZED' | 'SOLVED' | 'DELETED';

export type AlertEpisodeGroupId = 'IN_FOCUS' | 'ELSEWHERE' | 'NOT_ACTIVE';
export const ALERT_EPISODE_GROUPS: AlertEpisodeGroupId[] = ['IN_FOCUS', 'ELSEWHERE', 'NOT_ACTIVE'];

/** MERIE's alert lists as the BE sends them in an episode's component (free strings on the wire). */
export const ALERT_COMPONENT_HOSPITALIZATION_LIST = 'hospitalizationList';
export const ALERT_COMPONENT_PHARMACOTHERAPY_RECOMMENDATION_LIST = 'pharmacotherapyRecommendationList';

/** The translated enums of the displayed state, the category and the origin in `GET /api/enums`. The origin's values are
 * the component strings; it is MERIE's own enum. */
export const ALERT_DISPLAYED_STATE_ENUM = 'AlertDisplayedStateEnum';
export const ALERT_CATEGORY_ENUM = 'AlertCategoryEnum';
export const ALERT_ORIGIN_ENUM = 'AlertOriginEnum';

export type AlertEpisodeTooltipDto = {
  /** The saved-filter component the definition belongs to, e.g. hospitalizationList. */
  filterComponent: string;
  /** The filter criteria of the version the episode was evaluated against - raw JSON (object or string). */
  filterData: Record<string, unknown> | string;
  /** The episode's last change, selected by the BE per displayed state (ISO date-time). */
  lastChangeOn: string;
  /** The actor of the last change; null or missing means the system, the FE renders the product name. */
  lastChangeBy?: string | null;
};

/** The ids beside the anchor id that the anchor's page needs, named by MERIE (`AlertAnchorProperties`). */
export type AlertEpisodeAnchorProperties = {
  /** The hospitalization a pharmacotherapy recommendation belongs to. */
  evaluatedEncounterId?: number;
};

export type AlertEpisodeRowDto = {
  episodeId: number;
  userFilterId: number;
  name: string;
  isGlobal: boolean;
  /** Always sent; the FE hides the badge on non-active rows. */
  weight: number;
  displayedState: AlertEpisodeDisplayedState;
  /** ISO date, set when displayedState is SNOOZED ("Snoozed · DD.MM"). */
  snoozeExpiresOn?: string | null;
  /** The alert's list, which decides what the anchor is, e.g. hospitalizationList. */
  component: string;
  anchorId: number;
  /**
   * What else the anchor needs to be opened (PDW-405, BE PDW-417): a pharmacotherapy recommendation names its
   * hospitalization. Always sent, empty when the anchor id alone opens the page or the anchor does not resolve.
   */
  anchorProperties: AlertEpisodeAnchorProperties;
  /** False for a kept row whose source record no longer resolves: unlinked, no actions, neutral marker. */
  anchorAvailable: boolean;
  availableActions: AlertEpisodeAction[];
  tooltip: AlertEpisodeTooltipDto;
};

export type AlertEpisodeGroupDto = {
  group: AlertEpisodeGroupId;
  /** Rows in the group, snoozed included. */
  count: number;
  rows: AlertEpisodeRowDto[];
};

export type AlertSnoozeOptionDto = {
  days: number;
  /** ISO date the snooze would end if picked today; computed by the BE with the clock it stores the snooze with. */
  expiresOn: string;
};

export type AlertEpisodePanelDto = {
  /** Episodes displaying as ACTIVE (snoozed excluded) - the tab badge and the panel header. */
  activeCount: number;
  /** Always all three groups, in fixed order. */
  groups: AlertEpisodeGroupDto[];
  /** The five snooze durations with their dates; panel-level, identical for every row. */
  snoozeOptions?: AlertSnoozeOptionDto[];
};

export type AlertEpisodeDispositionPostDto = {
  disposition: AlertDisposition;
  /** Sent with a snooze and only with one, one of the snooze options' days. */
  snoozeDays?: number;
};

export type AlertEpisodeDispositionResponseDto = {
  episodeId: number;
  displayedState: AlertEpisodeDisplayedState;
  disposition: AlertDisposition;
  matchActive: boolean;
  snoozeExpiresOn?: string | null;
  availableActions: AlertEpisodeAction[];
};
